import { getLanguage } from './i18n.js';
import { normalizeRoutePath, parsePath, routeHref } from './url.js';

/**
 * History router.
 *
 * The server delivers a prerendered page for every route and locale. Client
 * navigation swaps the rendered view, updates the URL with pushState and lets
 * `afterRender` refresh the document metadata. In-page anchors (#main, #katalog)
 * keep their native behaviour.
 */
const routes = new Map();
const listeners = new Set();

let outlet = null;
let notFound = null;
let currentPath = null;

export const currentRoute = () => currentPath;

/** Build-time counterpart of the router state, used before a page is rendered. */
export const setCurrentPath = (path) => {
  currentPath = path;
};

export const defineRoutes = (definitions) => {
  definitions.forEach((route) => routes.set(route.path, route));
};

const hashId = (hash) => {
  const raw = hash.slice(1);
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

const scrollToLocation = () => {
  const hash = window.location.hash;
  if (hash && !hash.startsWith('#/')) {
    const target = document.getElementById(hashId(hash));
    if (target) {
      target.scrollIntoView({ block: 'start' });
      return;
    }
  }
  window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
};

async function handleChange(path, { scroll = true } = {}) {
  const route = routes.get(path);
  let renderedRoute = route;
  currentPath = route ? path : null;

  outlet.setAttribute('aria-busy', 'true');
  try {
    outlet.innerHTML = route ? await route.view() : notFound();
  } catch (error) {
    console.error(`Route "${path}" failed to render`, error);
    renderedRoute = null;
    currentPath = null;
    outlet.innerHTML = notFound(error);
  } finally {
    outlet.removeAttribute('aria-busy');
  }

  if (scroll) scrollToLocation();
  listeners.forEach((fn) => fn(path, renderedRoute));
}

export const navigate = (path) => {
  const routePath = normalizeRoutePath(path);
  const target = routeHref(routePath, getLanguage());
  if (window.location.pathname === target) return handleChange(routePath);
  window.history.pushState({}, '', target);
  return handleChange(routePath);
};

export const onNavigate = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

function handleClick(event) {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;

  const link = event.target.closest?.('a[href]');
  if (!link || link.target === '_blank' || link.hasAttribute('download')) return;

  const href = link.getAttribute('href');
  if (!href) return;

  // In-page anchor: move focus and scroll, never treat it as a route.
  if (href.startsWith('#')) {
    if (href.startsWith('#/')) {
      event.preventDefault();
      const parsed = parsePath(href.slice(1));
      navigate(parsed.path);
      return;
    }

    const target = document.getElementById(hashId(href));
    if (target) {
      event.preventDefault();
      target.focus({ preventScroll: true });
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    return;
  }

  if (/^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(href)) return;

  let url;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return;
  }
  if (url.origin !== window.location.origin) return;

  const parsed = parsePath(url.pathname);
  if (!routes.has(parsed.path)) return;

  // A link into another language needs its own dictionary chunk and <html lang>.
  if (parsed.locale !== getLanguage()) return;

  event.preventDefault();
  const target = `${url.pathname}${url.search}${url.hash}`;
  if (target === `${window.location.pathname}${window.location.search}${window.location.hash}`) {
    handleChange(parsed.path);
    return;
  }
  window.history.pushState({}, '', target);
  handleChange(parsed.path);
}

const handlePopState = () => {
  const parsed = parsePath(window.location.pathname);
  if (parsed.locale !== getLanguage()) {
    window.location.reload();
    return;
  }
  handleChange(parsed.path);
};

/** Keep old `#/games` bookmarks working and canonicalise them to `/games/`. */
const handleHashChange = () => {
  if (!window.location.hash.startsWith('#/')) return;
  const parsed = parsePath(window.location.hash.slice(1));
  if (!routes.has(parsed.path)) {
    handleChange(parsed.path);
    return;
  }
  const target = routeHref(parsed.path, parsed.locale);
  if (parsed.locale !== getLanguage()) {
    window.location.assign(target);
    return;
  }
  window.history.replaceState({}, '', target);
  handleChange(parsed.path);
};

export const startRouter = ({ mount, fallback, path = parsePath(window.location.pathname).path }) => {
  outlet = mount;
  notFound = fallback;
  window.addEventListener('popstate', handlePopState);
  window.addEventListener('hashchange', handleHashChange);
  document.addEventListener('click', handleClick);
  return handleChange(path);
};
